import { signNotifyApiAuthError } from "../initSignSuiteAuth";
import { getSignApiBase, readUserId } from "../suiteSession";

export class SignApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    message?: string;
  };
  if (signNotifyApiAuthError(res.status, data.error)) {
    throw new SignApiError("Session", res.status, data.error ?? "auth");
  }
  if (!res.ok || data.ok === false) {
    throw new SignApiError(data.message || data.error || `HTTP ${res.status}`, res.status, data.error ?? "error");
  }
  return data as T;
}

export async function signFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const userId = readUserId();
  if (userId) headers.set("X-User-Id", userId);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${getSignApiBase()}/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
    ...options,
    headers,
  });
  return parse<T>(res);
}

export function signUploadProgress<T>(
  path: string,
  body: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${getSignApiBase()}/${path.replace(/^\//, "")}`);
    xhr.withCredentials = true;
    const userId = readUserId();
    if (userId) xhr.setRequestHeader("X-User-Id", userId);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 92));
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || "{}") as {
          ok?: boolean;
          error?: string;
          message?: string;
        };
        if (signNotifyApiAuthError(xhr.status, data.error)) {
          reject(new SignApiError("Session", xhr.status, data.error ?? "auth"));
          return;
        }
        if (xhr.status >= 400 || data.ok === false) {
          reject(new SignApiError(data.message || data.error || `HTTP ${xhr.status}`, xhr.status, data.error ?? "error"));
          return;
        }
        onProgress?.(100);
        resolve(data as T);
      } catch {
        reject(new SignApiError("Réponse illisible.", xhr.status, "parse"));
      }
    };
    xhr.onerror = () => reject(new SignApiError("Réseau.", 0, "network"));
    xhr.send(body);
  });
}

export function signFileUrl(
  id: number,
  which: "original" | "sealed" | "proof" | "pack" = "original",
  opts?: { download?: boolean },
): string {
  const uid = readUserId();
  const q = new URLSearchParams({ id: String(id), which });
  if (uid) q.set("suite_user_id", uid);
  if (opts?.download) q.set("download", "1");
  return `${getSignApiBase()}/document-file.php?${q}`;
}

export function signDocFileWhich(doc: { status: string; has_sealed?: boolean }): "pack" | "sealed" | "original" {
  if (doc.status === "signed") return "pack";
  if (doc.has_sealed) return "sealed";
  return "original";
}

export async function fetchSignPackFile(doc: { id: number; status: string; has_sealed?: boolean; title?: string }): Promise<File> {
  const res = await fetch(signFileUrl(doc.id, signDocFileWhich(doc)), {
    credentials: "include",
    headers: readUserId() ? { "X-User-Id": readUserId() as string } : undefined,
  });
  if (!res.ok) throw new Error("PDF indisponible.");
  const blob = await res.blob();
  if (blob.size <= 0) throw new Error("PDF vide.");
  const name = `${(doc.title || "dossier-signe").replace(/[^\w.\-]+/g, "_")}.pdf`;
  return new File([blob], name, { type: "application/pdf" });
}

export function downloadSignFile(doc: { id: number; status: string; has_sealed?: boolean; title?: string }): void {
  const a = document.createElement("a");
  a.href = signFileUrl(doc.id, signDocFileWhich(doc), { download: true });
  a.download = `${(doc.title || "document").replace(/[^\w.\-]+/g, "_")}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function printSignFile(doc: { id: number; status: string; has_sealed?: boolean }): void {
  const url = signFileUrl(doc.id, signDocFileWhich(doc));
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.src = url;
  const fallback = () => window.open(url, "_blank", "noopener");
  const clean = () => frame.remove();
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      fallback();
    }
    window.setTimeout(clean, 120000);
  };
  frame.onerror = () => {
    fallback();
    clean();
  };
  document.body.appendChild(frame);
}

export function signPieceUrl(docId: number, pieceId: number): string {
  const uid = readUserId();
  const q = new URLSearchParams({ id: String(docId), which: "piece", pid: String(pieceId) });
  if (uid) q.set("suite_user_id", uid);
  return `${getSignApiBase()}/document-file.php?${q}`;
}
